import { useState, useLayoutEffect } from "react";

/**
 * While Tailwind does allow for pulling in the configuration and using the theme breakpoints, they
 * do mention that the result will pull in a lot of their build-time dependencies which can result
 * in bigger client side bundle size.
 *
 * This hook can achieve similar results without needing to pull in the Tailwind config. The downside
 * is that the breakpoints could become un-synced if we change them in the tailwind config.
 */

// This follows the default Tailwind responsive design.
// 2xs = 320px
// xs = 480px
// sm = 640px
// md = 768px
// lg = 1024px
// xl = 1280px
// 2xl = 1536px
type BreakpointIndicator = "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
type BreakpointResponse = { [key in BreakpointIndicator]: boolean };

function getActiveBreakpoints() {
  return {
    "2xs": window.matchMedia("(min-width: 320px)").matches,
    xs: window.matchMedia("(min-width: 480px)").matches,
    sm: window.matchMedia("(min-width: 640px)").matches,
    md: window.matchMedia("(min-width: 768px)").matches,
    lg: window.matchMedia("(min-width: 1024px)").matches,
    xl: window.matchMedia("(min-width: 1280px)").matches,
    "2xl": window.matchMedia("(min-width: 1536px)").matches,
  };
}

// Returns an object so that you can safely check multiple breakpoints directly (from lowest to highest)
// the same way you would do in Tailwind
export function useResponsiveBreakpoints(): BreakpointResponse {
  const [currentBreakpoints, setCurrentBreakpoints] =
    useState<BreakpointResponse>(getActiveBreakpoints());

  useLayoutEffect(() => {
    const updateSize = () => {
      setCurrentBreakpoints(getActiveBreakpoints());
    };

    window.addEventListener("resize", updateSize);

    return (): void => window.removeEventListener("resize", updateSize);
  });

  return currentBreakpoints;
}
